import { useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { ProjectWorkPhaseReport, WorkPhaseReportForm } from '../models/investors';
import {
  getWorkPhaseReportsEndpoint,
  createWorkPhaseReportEndpoint,
  updateWorkPhaseReportVisibilityEndpoint,
  uploadWorkPhaseReportMediaEndpoint,
  deleteWorkPhaseReportMediaEndpoint,
} from '../services/workPhaseReportsService';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';

const useProjectWorkPhaseReports = () => {
  const { callEndpoint } = useCallApiAndLoad();
  const [apiErrors, setApiErrors] = useState<string | null>(null);
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const doGetReports = async (
    projectId: string,
    workPhaseId: string,
  ): Promise<ProjectWorkPhaseReport[]> => {
    const { data, status } = await callEndpoint(
      getWorkPhaseReportsEndpoint(projectId, workPhaseId, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200) {
      setApiErrors(response.message);
      return [];
    }
    return Array.isArray(response.data) ? response.data : [];
  };

  const doCreateReport = async (
    projectId: string,
    workPhaseId: string,
    payload: WorkPhaseReportForm,
  ): Promise<number> => {
    const { data, status } = await callEndpoint(
      createWorkPhaseReportEndpoint(projectId, workPhaseId, payload, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 201) setApiErrors(response.message);
    return status;
  };

  const doSetVisibility = async (
    projectId: string,
    workPhaseId: string,
    reportId: string,
    isVisible: boolean,
  ): Promise<number> => {
    const { data, status } = await callEndpoint(
      updateWorkPhaseReportVisibilityEndpoint(projectId, workPhaseId, reportId, isVisible, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200) setApiErrors(response.message);
    return status;
  };

  const doUploadMedia = async (
    projectId: string,
    workPhaseId: string,
    reportId: string,
    formData: FormData,
  ): Promise<number> => {
    const { data, status } = await callEndpoint(
      uploadWorkPhaseReportMediaEndpoint(projectId, workPhaseId, reportId, formData, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200) setApiErrors(response.message);
    return status;
  };

  const doDeleteMedia = async (
    projectId: string,
    workPhaseId: string,
    reportId: string,
    path: string,
  ): Promise<number> => {
    const { data, status } = await callEndpoint(
      deleteWorkPhaseReportMediaEndpoint(projectId, workPhaseId, reportId, path, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200) setApiErrors(response.message);
    return status;
  };

  return {
    apiErrors,
    doGetReports,
    doCreateReport,
    doSetVisibility,
    doUploadMedia,
    doDeleteMedia,
  };
};

export default useProjectWorkPhaseReports;
